<template>
  <div class="verificar-page">
    <!-- Header -->
    <header class="verificar-header">
      <div class="verificar-header-content">
        <router-link to="/" class="verificar-logo">
          <div class="verificar-logo-icon">OA</div>
          <span class="verificar-logo-text">Olympic Academy</span>
        </router-link>
        <span class="verificar-header-label">Verificacion de Certificado</span>
      </div>
    </header>

    <main class="verificar-main">
      <!-- Loading state -->
      <div v-if="loading" class="verificar-loading">
        <div class="verificar-spinner" />
        <p class="verificar-loading-text">Verificando certificado...</p>
      </div>

      <!-- Not found state -->
      <div v-else-if="notFound" class="verificar-not-found">
        <div class="not-found-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </div>
        <h1 class="not-found-title">Certificado no encontrado</h1>
        <p class="not-found-text">
          El numero de certificado <span class="not-found-number">{{ decodedCertNumber }}</span> no existe en nuestro sistema.
        </p>
        <router-link to="/" class="not-found-link">
          Ir al inicio
        </router-link>
      </div>

      <!-- Certificate found -->
      <template v-else-if="cert">
        <!-- Status badge -->
        <div class="verificar-status-center">
          <div class="verificar-status-badge" :class="isValid ? 'status-valid' : 'status-invalid'">
            <svg v-if="isValid" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22c55e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <span class="verificar-status-label" :class="'status-text-' + effectiveStatus">
              {{ statusLabel }}
            </span>
          </div>
        </div>

        <!-- Certificate card -->
        <div class="verificar-card">
          <!-- Gold header -->
          <div class="verificar-card-header">
            <p class="verificar-card-org">Olympic Academy</p>
            <h1 class="verificar-card-level">{{ levelLabel }}</h1>
            <p class="verificar-card-number">{{ cert.certNumber }}</p>
          </div>

          <!-- Body -->
          <div class="verificar-card-body">
            <!-- Holder -->
            <div class="verificar-holder">
              <p class="verificar-holder-label">Otorgado a</p>
              <p class="verificar-holder-name">{{ cert.holderName }}</p>
            </div>

            <!-- Details grid -->
            <div class="verificar-details-grid">
              <div class="verificar-detail-item">
                <p class="verificar-detail-label">Fecha de emision</p>
                <p class="verificar-detail-value">
                  {{ cert.issuedAt ? formatDateLong(cert.issuedAt) : 'Pendiente' }}
                </p>
              </div>
              <div class="verificar-detail-item">
                <p class="verificar-detail-label">Fecha de vencimiento</p>
                <p class="verificar-detail-value">
                  {{ cert.expiresAt ? formatDateLong(cert.expiresAt) : 'Sin vencimiento' }}
                </p>
              </div>
            </div>

            <!-- Level -->
            <div class="verificar-level-item">
              <p class="verificar-detail-label">Nivel de certificacion</p>
              <p class="verificar-level-value">{{ cert.level.replace(/_/g, ' ').toUpperCase() }}</p>
            </div>

            <!-- Grace period warning -->
            <div v-if="effectiveStatus === 'en_gracia' && cert.graceEndsAt" class="verificar-grace-warning">
              <p class="verificar-grace-label">Periodo de gracia hasta</p>
              <p class="verificar-grace-date">{{ formatDateLong(cert.graceEndsAt) }}</p>
            </div>
          </div>

          <!-- Footer -->
          <div class="verificar-card-footer">
            <p class="verificar-footer-text">El Templo Calistenia — Olympic Academy</p>
          </div>
        </div>

        <!-- Info text -->
        <p class="verificar-info-text">
          Este certificado fue verificado en tiempo real contra la base de datos de Olympic Academy.
          <br />
          Para consultas, contactar a administracion@eltemplocalistenia.com
        </p>
      </template>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';

// ── Types ──

interface CertVerifyData {
  certNumber: string;
  level: string;
  status: string;
  holderName: string;
  issuedAt: string | null;
  expiresAt: string | null;
  graceEndsAt?: string | null;
  isValid: boolean;
}

// ── Constants ──

const LEVEL_LABELS: Record<string, string> = {
  cc: 'Coach Certified',
  sc: 'Senior Coach',
  c1: 'Coach Instructor',
  c2: 'Lead Trainer',
  ot_1: 'Olympic Trainer',
  owner: 'Owner/Partner',
  oa: 'Olympic Auditor',
  gj: 'Games Judge',
  kairos: 'Kairos (Poblaciones Especiales)',
};

const STATUS_LABELS: Record<string, string> = {
  certificado: 'Certificado Valido',
  en_progreso: 'En Progreso',
  requisitos_cumplidos: 'Requisitos Cumplidos',
  expirado: 'Expirado',
  en_gracia: 'Periodo de Gracia',
  revocado: 'Revocado',
};

// ── State ──

const route = useRoute();
const loading = ref(true);
const notFound = ref(false);
const cert = ref<CertVerifyData | null>(null);

// ── Computed ──

const decodedCertNumber = computed(() =>
  decodeURIComponent(String(route.params.certNumber ?? '')),
);

const effectiveStatus = computed(() => {
  if (!cert.value) return '';
  if (cert.value.expiresAt && new Date(cert.value.expiresAt) < new Date() && cert.value.status === 'certificado') {
    return 'expirado';
  }
  return cert.value.status;
});

const isValid = computed(() =>
  effectiveStatus.value === 'certificado',
);

const statusLabel = computed(() =>
  STATUS_LABELS[effectiveStatus.value] ?? 'Desconocido',
);

const levelLabel = computed(() =>
  LEVEL_LABELS[cert.value?.level ?? ''] ?? cert.value?.level ?? '',
);

// ── Methods ──

function formatDateLong(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

async function loadCertificate() {
  loading.value = true;
  notFound.value = false;
  try {
    // Use plain axios (no auth) for public verification endpoint — T-07-14
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
    const certNumber = encodeURIComponent(decodedCertNumber.value);
    const { data } = await axios.get(`${baseUrl}/certification/verify/${certNumber}`);
    cert.value = data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      notFound.value = true;
    } else {
      console.error('[verificar] loadCertificate error:', err);
      notFound.value = true;
    }
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  loadCertificate();
});
</script>

<style scoped>
.verificar-page {
  min-height: 100vh;
  background: linear-gradient(to bottom, #F5F0E8, var(--color-marble-cream));
}

/* Header */
.verificar-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(8px);
}

.verificar-header-content {
  max-width: 640px;
  margin: 0 auto;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.verificar-logo {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
}

.verificar-logo-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--color-aged-gold);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-warm-linen);
  font-weight: 700;
  font-size: 12px;
}

.verificar-logo-text {
  color: var(--color-aged-gold);
  font-weight: 600;
  font-size: 14px;
}

.verificar-header-label {
  font-size: 12px;
  color: rgba(26, 25, 23, 0.3);
}

/* Main */
.verificar-main {
  max-width: 640px;
  margin: 0 auto;
  padding: 48px 16px;
}

/* Loading */
.verificar-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 80px 0;
}

.verificar-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(201, 169, 110, 0.2);
  border-top-color: var(--color-aged-gold);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.verificar-loading-text {
  font-size: 14px;
  color: rgba(26, 25, 23, 0.5);
}

/* Not found */
.verificar-not-found {
  text-align: center;
  padding: 40px 0;
}

.not-found-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: #fef2f2;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
}

.not-found-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--color-warm-linen);
  margin: 0 0 8px 0;
}

.not-found-text {
  color: rgba(26, 25, 23, 0.5);
  margin: 0 0 24px 0;
  font-size: 14px;
}

.not-found-number {
  font-family: monospace;
}

.not-found-link {
  display: inline-block;
  padding: 10px 24px;
  background: var(--color-aged-gold);
  color: var(--color-marble-cream);
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: background 0.2s;
}

.not-found-link:hover {
  filter: brightness(0.9);
}

/* Status badge */
.verificar-status-center {
  text-align: center;
  margin-bottom: 32px;
}

.verificar-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 999px;
  border: 1px solid;
}

.status-valid {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.status-invalid {
  background: #fffbeb;
  border-color: #fde68a;
}

.verificar-status-label {
  font-size: 14px;
  font-weight: 600;
}

.status-text-certificado {
  color: #15803d;
}

.status-text-en_progreso {
  color: #1d4ed8;
}

.status-text-requisitos_cumplidos {
  color: #b45309;
}

.status-text-expirado {
  color: #b91c1c;
}

.status-text-en_gracia {
  color: #c2410c;
}

.status-text-revocado {
  color: #374151;
}

/* Certificate card */
.verificar-card {
  background: var(--color-marble-cream);
  border-radius: 24px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.verificar-card-header {
  background: linear-gradient(to right, var(--color-aged-gold), #D4B87A, var(--color-aged-gold));
  padding: 24px 32px;
  text-align: center;
}

.verificar-card-org {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.2em;
  margin: 0 0 4px 0;
}

.verificar-card-level {
  font-size: 24px;
  font-weight: 700;
  color: var(--color-marble-cream);
  margin: 0;
}

.verificar-card-number {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  font-family: monospace;
  margin: 4px 0 0 0;
}

.verificar-card-body {
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.verificar-holder {
  text-align: center;
}

.verificar-holder-label {
  font-size: 12px;
  color: rgba(26, 25, 23, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin: 0 0 4px 0;
}

.verificar-holder-name {
  font-size: 20px;
  font-weight: 600;
  color: var(--color-warm-linen);
  margin: 0;
}

.verificar-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.verificar-detail-item {
  background: rgba(245, 240, 232, 0.5);
  border-radius: 12px;
  padding: 16px;
  text-align: center;
}

.verificar-detail-label {
  font-size: 12px;
  color: rgba(26, 25, 23, 0.4);
  margin: 0 0 4px 0;
}

.verificar-detail-value {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-warm-linen);
  margin: 0;
}

.verificar-level-item {
  background: rgba(245, 240, 232, 0.5);
  border-radius: 12px;
  padding: 16px;
  text-align: center;
}

.verificar-level-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--color-aged-gold);
  text-transform: uppercase;
  margin: 0;
}

.verificar-grace-warning {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 12px;
  padding: 16px;
  text-align: center;
}

.verificar-grace-label {
  font-size: 12px;
  color: #c2410c;
  margin: 0 0 4px 0;
}

.verificar-grace-date {
  font-size: 14px;
  font-weight: 500;
  color: #9a3412;
  margin: 0;
}

/* Footer */
.verificar-card-footer {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  padding: 16px 32px;
  background: rgba(245, 240, 232, 0.3);
  text-align: center;
}

.verificar-footer-text {
  font-size: 10px;
  color: rgba(26, 25, 23, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin: 0;
}

/* Info text */
.verificar-info-text {
  text-align: center;
  font-size: 12px;
  color: rgba(26, 25, 23, 0.3);
  margin-top: 32px;
  line-height: 1.6;
}

/* Mobile */
@media (max-width: 480px) {
  .verificar-main {
    padding: 24px 12px;
  }

  .verificar-details-grid {
    grid-template-columns: 1fr;
  }

  .verificar-card-body {
    padding: 20px;
  }

  .verificar-card-header {
    padding: 20px;
  }
}
</style>
